import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Conversation {
  id: string;
  request_code: string;
  subject: string;
  status: string;
  created_at: string;
}

interface ChatMessage {
  id: string;
  content: string;
  is_staff: boolean;
  created_at: string;
  attachment_url: string | null;
  attachment_name: string | null;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return 'Hôm nay';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
}

function isImageUrl(url: string | null): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.gif') || lower.endsWith('.webp') || lower.endsWith('.svg') || lower.endsWith('.bmp') || lower.endsWith('.avif');
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const WELCOME_TEXT = 'Chào mừng bạn đến dịch vụ chăm sóc khách hàng của GOLDSBV. Chúng tôi có thể hỗ trợ gì cho bạn?';
export default function ChatWindow({ bare = false }: { bare?: boolean }) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [isOnline, setIsOnline] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const apiUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/chat-support`;

  const fetchConversation = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Vui lòng đăng nhập để sử dụng chat');
        setLoading(false);
        return;
      }

      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'get_conversation' }),
      });
      const result = await resp.json();
      if (!resp.ok || result.error) {
        throw new Error(result.error || 'Không thể tải cuộc trò chuyện');
      }
      setConversation(result.conversation);
      if (result.conversation?.id) {
        await fetchMessages(result.conversation.id, token);
      }
    } catch (e: any) {
      setError(e.message || 'Không thể kết nối chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId: string, token: string) => {
    const resp = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action: 'get_messages', request_id: requestId }),
    });
    const result = await resp.json();
    if (resp.ok && result.messages) {
      setMessages(result.messages);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Vui lòng chọn tệp hình ảnh (JPG, PNG, GIF...)');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('Ảnh quá lớn. Vui lòng chọn ảnh dưới 5MB.');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const removeSelectedFile = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file: File, userId: string): Promise<{ url: string; name: string } | null> => {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop() || 'jpg';
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `chat/${userId}/${timestamp}-${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from('support-attachments')
      .upload(path, file, {
        contentType: file.type,
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Upload thất bại: ${uploadError.message}`);
    }

    const { data: urlData } = supabase.storage.from('support-attachments').getPublicUrl(path);
    return { url: urlData.publicUrl, name: file.name };
  };

  const sendMessage = async () => {
    if (!conversation?.id || sending) return;
    const hasText = input.trim().length > 0;
    const hasImage = !!selectedFile;
    if (!hasText && !hasImage) return;

    setSending(true);
    setUploadingImage(hasImage);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const userId = sessionData.session?.user?.id;
      if (!token || !userId) throw new Error('Unauthorized');

      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;

      if (selectedFile) {
        const uploadResult = await uploadImage(selectedFile, userId);
        if (uploadResult) {
          attachmentUrl = uploadResult.url;
          attachmentName = uploadResult.name;
        }
        // Dọn preview sau khi upload xong
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
        setSelectedFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const text = input.trim();
      if (text) setInput('');

      const resp = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'send_message',
          request_id: conversation.id,
          content: text || '',
          attachment_url: attachmentUrl,
          attachment_name: attachmentName,
        }),
      });
      const result = await resp.json();
      if (!resp.ok || result.error) {
        throw new Error(result.error || 'Không thể gửi tin nhắn');
      }
      if (result.message) {
        setMessages((prev) => [...prev, result.message]);
      }
    } catch (e: any) {
      setError(e.message || 'Gửi tin nhắn thất bại');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSending(false);
      setUploadingImage(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial load
  useEffect(() => {
    fetchConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!conversation?.id) return;
    const channel = supabase
      .channel(`live-chat-${conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_replies',
          filter: `request_id=eq.${conversation.id}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMessage;
          if (newMsg?.is_staff) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id]);

  // Online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const hasStaffMessage = messages.some((m) => m.is_staff);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground-500">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Đang kết nối...</span>
        </div>
      </div>
    );
  }

  if (error && !conversation) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-chat-off-line text-2xl text-rose-500"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">{error}</p>
          <Link
            to="/dang-nhap"
            className="mt-3 inline-block px-4 py-2 rounded-md bg-primary-500 text-background-950 text-sm font-semibold cursor-pointer hover:bg-primary-600"
          >
            Đăng nhập
          </Link>
        </div>
      </div>
    );
  }

  const canSend = input.trim().length > 0 || !!selectedFile;

  return (
    <div className={`flex-1 flex flex-col overflow-hidden min-h-0 ${bare ? '' : 'rounded-xl border border-background-200/60 bg-background-100'}`}>
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-3 shrink-0">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-customer-service-2-line text-lg text-primary-600"></i>
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background-100 ${isOnline ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950">Hỗ trợ viên</p>
          <p className="text-xs text-foreground-500">
            {isOnline ? 'Trực tuyến' : 'Mất kết nối'}
            {conversation && (
              <span className="ml-2 text-foreground-400">· Mã: {conversation.request_code}</span>
            )}
          </p>
        </div>
        <Link
          to="/lien-he"
          className="px-3 py-1.5 rounded-md text-xs font-semibold text-foreground-600 border border-background-300/60 hover:text-primary-400 cursor-pointer transition-colors whitespace-nowrap"
        >
          Gửi yêu cầu
        </Link>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {/* Welcome message — always auto-reply from staff at top */}
        <div className="flex justify-start">
          <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-background-200 text-foreground-900 rounded-bl-md">
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{WELCOME_TEXT}</p>
            <p className="text-[10px] mt-1 text-foreground-500">{formatTime(new Date().toISOString())}</p>
          </div>
        </div>

        {messages.map((msg, index) => {
          const showDate = index === 0 || formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
          const isMe = !msg.is_staff;
          const hasImage = isImageUrl(msg.attachment_url);

          return (
            <div key={msg.id}>
              {showDate && (
                <div className="flex items-center justify-center my-3">
                  <span className="text-[11px] text-foreground-400 bg-background-200/50 px-3 py-1 rounded-full">
                    {formatDate(msg.created_at)}
                  </span>
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  isMe
                    ? 'bg-primary-500 text-background-950 rounded-br-md'
                    : 'bg-background-200 text-foreground-900 rounded-bl-md'
                }`}>
                  {msg.content && (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  )}
                  {hasImage && msg.attachment_url && (
                    <button
                      onClick={() => setLightboxUrl(msg.attachment_url)}
                      className="mt-2 block w-full rounded-lg overflow-hidden cursor-pointer border border-white/20"
                    >
                      <img
                        src={msg.attachment_url}
                        alt={msg.attachment_name || 'Hình ảnh'}
                        className="w-full max-h-48 object-cover"
                      />
                    </button>
                  )}
                  {msg.attachment_url && !hasImage && (
                    <a
                      href={msg.attachment_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-2 block text-xs font-semibold underline break-all"
                    >
                      <i className="ri-attachment-2 mr-1"></i>
                      {msg.attachment_name || 'Tệp đính kèm'}
                    </a>
                  )}
                  <p className={`text-[10px] mt-1 ${isMe ? 'text-primary-300' : 'text-foreground-500'}`}>
                    {formatTime(msg.created_at)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Lightbox / image viewer */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img
              src={lightboxUrl}
              alt="Xem ảnh"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="px-4 py-3 border-t border-background-200/60 shrink-0">
        {error && (
          <p className="text-xs text-rose-500 mb-2">{error}</p>
        )}

        {/* Preview selected image */}
        {imagePreview && (
          <div className="mb-2 inline-flex items-center gap-2 rounded-lg border border-background-200/60 bg-background-50 px-2 py-2 max-w-full">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-12 h-12 rounded object-cover shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-foreground-700 truncate">{selectedFile?.name}</p>
              <p className="text-[10px] text-foreground-500">
                {selectedFile ? (selectedFile.size / 1024).toFixed(1) : 0} KB
              </p>
            </div>
            <button
              onClick={removeSelectedFile}
              className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center cursor-pointer hover:bg-rose-100 shrink-0"
              title="Xóa ảnh"
            >
              <i className="ri-close-line text-xs text-foreground-600"></i>
            </button>
          </div>
        )}

        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="w-10 h-10 rounded-full bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer hover:bg-background-300 disabled:opacity-50 shrink-0"
            title="Gửi ảnh"
          >
            <i className="ri-image-line text-lg"></i>
          </button>

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder="Nhập tin nhắn..."
            className="flex-1 px-3 py-2.5 rounded-xl bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none min-h-[42px] max-h-[120px]"
            style={{ overflow: 'hidden' }}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !canSend}
            className="w-10 h-10 rounded-full bg-primary-500 text-background-950 flex items-center justify-center cursor-pointer hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {sending || uploadingImage ? (
              <div className="w-4 h-4 border-2 border-background-950 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <i className="ri-send-plane-fill text-lg"></i>
            )}
          </button>
        </div>
        <p className="text-[10px] text-foreground-400 mt-1.5">Nhấn Enter để gửi, Shift + Enter để xuống dòng</p>
      </div>
    </div>
  );
}